// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyPlansPagePivotType } from "@ms/uno-constants/lib/local/ViewPivots";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { applyComparator, applyFilter, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
import { ProjectMetadataOrgType } from "@ms/planner-models/lib/local/service/mru/enumerations/MruEnumerations";
import { MruApp } from "@ms/planner-models/lib/local/service/mru/MruApp";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { UpdatableSlice } from "../../slice/UpdatableSlice";
import { Store } from "../Store";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import { isMruItemAPlan } from "@ms/planner-models/lib/local/utilities/MruAppUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
// Mru index names
var MruIndex = /*#__PURE__*/ function(MruIndex) {
    MruIndex["MruId"] = "mruId";
    MruIndex["Data"] = "data";
    MruIndex["View"] = "view";
    return MruIndex;
}(MruIndex || {});
// Mru index partition keys
var MruPartitionKey = /*#__PURE__*/ function(MruPartitionKey) {
    /** Partition for recent items */ MruPartitionKey["Recent"] = "recent";
    /** Partition for pinned items */ MruPartitionKey["Pinned"] = "pinned";
    return MruPartitionKey;
}(MruPartitionKey || {});
// View index/partition constants
const MyPlansViewPartition = "MyPlansView";
const MyPlansViewPivotIndex = "myPlansViewPivot";
/**
 * Store for MRU data
 */ export class MruStore extends EventPublishingStore {
    get name() {
        return Store.Mru;
    }
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.Mru, {
            [TableName.Mru]: this.mruSlice.table(),
            ...this.mruSlice.statusTables()
        });
    }
    getMruItemById(mruId) {
        const itemsByMruId = this.mruTable.index("mruId").partition(mruId).items();
        return itemsByMruId.length > 0 ? itemsByMruId[0].value : null;
    }
    getMruItemByServerDocId(serverDocId, readBaseline) {
        const entity = this.mruTable.get(serverDocId);
        return entity && entity.status === ExistenceStatus.Created ? readBaseline ? entity.baseline : entity.value : null;
    }
    getRecentItems() {
        return this.mruSlice.getCollection({
            name: "data",
            value: "recent"
        });
    }
    getPinnedItems() {
        return {
            data: this.mruTable.index("data").partition("pinned").items().map((item)=>item.value),
            // Pinned items are not fetched independently and hence their status is identical to the recent items
            status: this.getRecentItems().status
        };
    }
    getRecentItemsForView(viewId) {
        switch(viewId.name){
            case ViewName.MyPlansPage:
                return this.getMyPlansViewPartition().index(MyPlansViewPivotIndex).partition(viewId.partitionKey).items().map((item)=>item.value);
            default:
                throw new Error(`MruPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchMruItems:
                storeChanged = this.mruSlice.handleCollectionFetchStarted({
                    name: "data",
                    value: "recent"
                });
                break;
            case ActionType.FetchMruItemsFailed:
                storeChanged = this.mruSlice.handleCollectionFetchFailed({
                    name: "data",
                    value: "recent"
                }, action.response);
                break;
            case ActionType.FetchMruItemsSucceeded:
                storeChanged = this.handleFetchMruItemsSucceeded(action);
                break;
            case ActionType.CreateMruItem:
                storeChanged = this.mruSlice.handleCreateStarted(action.mruItem.serverDocId, action.mruItem);
                this.creations.add(action.mruItem.serverDocId);
                break;
            case ActionType.CreateMruItemFailed:
                storeChanged = this.mruSlice.handleCreateFailed(action.mruItem.serverDocId);
                break;
            case ActionType.CreateMruItemSucceeded:
                storeChanged = this.handleCreateMruItemSucceeded(action);
                break;
            case ActionType.UpdateMruItem:
                storeChanged = this.mruSlice.handleUpdateStarted(action.serverDocId, action.updateKey, action.delta);
                break;
            case ActionType.UpdateMruItemFailed:
                storeChanged = this.mruSlice.handleUpdateFailed(action.serverDocId, action.updateKey, action.response);
                break;
            case ActionType.UpdateMruItemSucceeded:
                storeChanged = this.mruSlice.handleUpdateSucceeded(action.updatedMruItem.serverDocId, action.updateKey, action.updatedMruItem);
                break;
            case ActionType.DeleteMruItem:
                storeChanged = this.mruSlice.handleDeleteStarted(action.serverDocId);
                this.deletions.add(action.serverDocId);
                break;
            case ActionType.DeleteMruItemFailed:
                storeChanged = this.mruSlice.handleDeleteFailed(action.deletedMruItem.serverDocId, action.response);
                break;
            case ActionType.DeleteMruItemSucceeded:
                storeChanged = this.mruSlice.handleDeleteSucceeded(action.serverDocId);
                break;
            case ActionType.SetPlanFilterAction:
                storeChanged = this.handleSetPlanFilterAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Handle successful fetch of MRU items.
     */ handleFetchMruItemsSucceeded(action) {
        const items = action.mruItems.filter((item)=>!this.deletions.has(item.serverDocId));
        let storeChanged = this.mruSlice.handleCollectionFetchSucceeded({
            name: "data",
            value: "recent"
        }, true, Object.fromEntries(items.map((item)=>[
                item.serverDocId,
                item
            ])), []);
        for (const mruItem of items){
            const storeMruItem = this.mruTable.get(mruItem.serverDocId);
            if (storeMruItem && mruItem.isPinned !== storeMruItem.baseline.isPinned && (Temporal.Instant.compare(storeMruItem.baseline.timestamp, mruItem.timestamp) === 0 || this.creations.has(mruItem.serverDocId))) {
                // If the item was fetched and the fetched item has a different pinned state, update the store copy
                // This can happen if the fetch happens after the create has finished but has the latest data from the create (new timestamp)
                // client sends create
                // client gets back create response with the latest timestamp
                // client sends fetch
                // client gets back fetch where the item is pinned and has new timestamp
                // If the item was created locally and the fetched item has a different pinned state, update the store copy
                // Another case can also happen if the fetch happens before the create has finished
                // client sends fetch
                // client sends create
                // client gets back fetch where the item is pinned but has an old timestamp (fetch doesn't have latest create timestamp)
                // client gets back create response with the latest timestamp
                // In this case, the create has the latest data but is missing pinned state, so only sync pinned state to the baseline
                const newItem = storeMruItem.baseline.getClone().setProperty("isPinned", mruItem.isPinned);
                storeMruItem.updateBaseline(newItem);
                storeChanged = true;
            }
        }
        return storeChanged;
    }
    /**
     * Handle successful creation of an MRU item.
     */ handleCreateMruItemSucceeded(action) {
        let newItem = action.mruItem;
        const storeCopy = this.mruTable.get(action.mruItem.serverDocId);
        // If the fetched item has a different pinned state, keep the one from the store
        if (storeCopy && storeCopy.baseline.isPinned !== action.mruItem.isPinned) {
            newItem = action.mruItem.setProperty("isPinned", storeCopy.baseline.isPinned);
        }
        return this.mruSlice.handleCreateSucceeded(action.mruItem.serverDocId, newItem, action.mruItem.serverDocId);
    }
    /**
     * Handle application of plan filter on the view
     */ handleSetPlanFilterAction(action) {
        const keyword = `${action.filter?.filters?.find((filter)=>{
            return filter.field === "name";
        })?.values?.[0] ?? ""}`.toLocaleLowerCase();
        applyFilter(this.getMyPlansViewPartition(), (item)=>item.title.toLocaleLowerCase().indexOf(keyword) !== -1);
        return true;
    }
    // Comparator for mru items based on timestamp (most recent first)
    mruItemComparator(a, b) {
        return Temporal.Instant.compare(b.timestamp, a.timestamp);
    }
    // Helper to get the partition for MyPlans view
    getMyPlansViewPartition() {
        return this.mruTable.index("view").partition(MyPlansViewPartition);
    }
    /**
     * Get data partition keys for a given mru item
     */ getMruItemDataPartitions(mruItem) {
        if (!this.mruItemEntityFilterPredicate(mruItem)) {
            return [];
        }
        // All mru items go into a "recent" partition
        const partitions = [
            "recent"
        ];
        // Pinned items also go into a "pinned" partition
        if (mruItem.isPinned) {
            partitions.push("pinned");
        }
        return partitions;
    }
    /**
     * Get the plan view pivots for a given plan MRU item.
     */ getMyPlansPivotsForMruItem(mruItem) {
        const pivots = [];
        if (!this.mruItemEntityFilterPredicate(mruItem) || !isMruItemAPlan(mruItem)) {
            return pivots;
        }
        // All recent plans go into the "recent" pivot
        pivots.push(MyPlansPagePivotType.Recent);
        // All shared plans go into the "shared" pivot, while personal plans go into the "personal" pivot
        const isShared = mruItem.isShared();
        if (isShared !== null) {
            pivots.push(isShared ? MyPlansPagePivotType.Shared : MyPlansPagePivotType.Personal);
        }
        // All pinned plans go into the "pinned" pivot
        if (mruItem.isPinned) {
            pivots.push(MyPlansPagePivotType.Pinned);
        }
        return pivots;
    }
    // Helper to get the MRU table from the slice
    get mruTable() {
        return this.mruSlice.table();
    }
    constructor(loggers){
        super(), this.loggers = loggers, /**
     * MRU slice partition structure
     *
     * ```
     * ☶ Root Table: All MRU items // <-- Recency based sort applied at the root level
     * ├── MruId
     * │   ├── ☶ mruId1
     * │   └── ☶ mruId2 (one partition for each mruId)
     * │
     * ├── Data
     * │   ├── ☶ Recent
     * │   └── ☶ Pinned
     * │
     * └── View
     *     └── ☶ MyPlansView // <-- Keyword filter applied to all view partitions
     *         └── Pivot
     *             ├── ☶ Recent
     *             ├── ☶ Pinned
     *             ├── ☶ Shared
     *             └── ☶ Personal
     * ```
     */ this.mruSlice = new UpdatableSlice(TableName.Mru, {
            /** Index for querying items by mruId (TODO: Remove this if not needed anymore) */ ["mruId"]: (item)=>item.mruId,
            /** Index for serving mru data queries */ ["data"]: (item)=>this.getMruItemDataPartitions(item),
            /** Index for serving view queries */ ["view"]: (item)=>MyPlansViewPartition
        }, // Items with a more recent timestamp are newer
        (baseline, newItem)=>this.mruItemComparator(baseline, newItem) > 0), // Ids that were created or deleted in this session
        this.creations = new Set(), this.deletions = new Set(), /**
     * Filter predicate for mru items entities in the mruStore
     * @param mruItem Mru item entity to filter
     */ this.mruItemEntityFilterPredicate = (mruItem)=>{
            if (mruItem.app !== MruApp.Project) {
                return true;
            }
            const metadata = mruItem.metadata;
            if (metadata.orgType === ProjectMetadataOrgType.None) {
                this.loggers.traceLogger.logTrace(0x1e19c41d /* tag_4g2q3 */ , TraceLevel.Info, "Plan org is of type none");
                return false;
            }
            if (metadata.orgType === ProjectMetadataOrgType.Named) {
                return true;
            }
            if (mruItem.isProjectOnlinePlan()) {
                return true;
            }
            if (mruItem.isProjectOperationsPlan()) {
                return true;
            }
            return metadata.orgType === ProjectMetadataOrgType.Default;
        };
        // All mru read results should be sorted by recency
        applyComparator(this.mruTable, this.mruItemComparator);
        // Register an index on the MyPlansView partition for view pivots so that we have a partition for each pivot
        registerIndex(this.getMyPlansViewPartition(), MyPlansViewPivotIndex, (mruItem)=>this.getMyPlansPivotsForMruItem(mruItem));
    }
}
